import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  Mail, 
  Trash2, 
  Eye, 
  X, 
  Clock, 
  User, 
  Phone,
  MessageSquare,
  CheckCircle,
  Circle,
  Search,
  RefreshCw
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import AdminLayout from "./AdminLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminMessagesPage = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState("all"); // all, unread, read

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/contact-messages`);
      setMessages(response.data);
    } catch (error) {
      console.error("Error fetching messages:", error);
      toast.error("Failed to load messages");
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (id) => {
    try {
      await axios.put(`${API}/admin/contact-messages/${id}/read`);
      setMessages(prev => prev.map(msg => 
        msg.id === id ? { ...msg, read: true } : msg
      ));
      if (selectedMessage?.id === id) {
        setSelectedMessage(prev => ({ ...prev, read: true }));
      }
      toast.success("Marked as read");
    } catch (error) {
      toast.error("Failed to update message");
    }
  };

  const markAsUnread = async (id) => {
    try {
      await axios.put(`${API}/admin/contact-messages/${id}/unread`);
      setMessages(prev => prev.map(msg => 
        msg.id === id ? { ...msg, read: false } : msg
      ));
      if (selectedMessage?.id === id) {
        setSelectedMessage(prev => ({ ...prev, read: false }));
      }
      toast.success("Marked as unread");
    } catch (error) {
      toast.error("Failed to update message");
    }
  };

  const deleteMessage = async (id) => {
    if (!window.confirm("Are you sure you want to delete this message?")) return;
    
    try {
      await axios.delete(`${API}/admin/contact-messages/${id}`);
      setMessages(prev => prev.filter(msg => msg.id !== id));
      if (selectedMessage?.id === id) {
        setSelectedMessage(null);
      }
      toast.success("Message deleted");
    } catch (error) {
      toast.error("Failed to delete message");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffHours < 24) {
      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    } else if (diffDays < 7) {
      return date.toLocaleDateString('en-US', { weekday: 'short', hour: 'numeric', minute: '2-digit' });
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }
  };

  const filteredMessages = messages.filter(msg => {
    const matchesSearch = 
      msg.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      msg.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      msg.message?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      msg.subject?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = 
      filter === "all" || 
      (filter === "unread" && !msg.read) ||
      (filter === "read" && msg.read);

    return matchesSearch && matchesFilter;
  });

  const unreadCount = messages.filter(m => !m.read).length;

  return (
    <AdminLayout>
      <div className="p-6" data-testid="admin-messages-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-['Playfair_Display'] font-bold text-[#F5F5F0]">
              Contact Messages
            </h1>
            <p className="text-[#A1A1AA] mt-1">
              {unreadCount > 0 ? `${unreadCount} unread message${unreadCount > 1 ? 's' : ''}` : 'All messages read'}
            </p>
          </div>

          <button
            onClick={fetchMessages}
            className="btn-secondary flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>

        {/* Filters and Search */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#a1a1aa]" />
            <input
              type="text"
              placeholder="Search messages..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-[#121212] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] font-medium placeholder-[#71717a] focus:border-[#D4AF37] focus:outline-none"
            />
          </div>

          <div className="flex border border-[rgba(212,175,55,0.2)] rounded overflow-hidden">
            {[
              { key: 'all', label: 'All' },
              { key: 'unread', label: 'Unread' },
              { key: 'read', label: 'Read' }
            ].map(f => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`px-4 py-2 text-sm transition-colors ${
                  filter === f.key
                    ? 'bg-[#D4AF37] text-[#050505]'
                    : 'bg-[#121212] text-[#A1A1AA] hover:text-[#D4AF37]'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="spinner" />
          </div>
        ) : messages.length === 0 ? (
          <div className="bg-[#121212] border border-[rgba(212,175,55,0.15)] p-12 text-center">
            <Mail className="w-16 h-16 text-[#a1a1aa] mx-auto mb-4" />
            <h3 className="text-xl font-['Playfair_Display'] text-[#F5F5F0] mb-2">No Messages Yet</h3>
            <p className="text-[#A1A1AA]">Messages from your contact form will appear here.</p>
          </div>
        ) : (
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Messages List */}
            <div className="bg-[#121212] border border-[rgba(212,175,55,0.15)] overflow-hidden">
              <div className="max-h-[600px] overflow-y-auto">
                {filteredMessages.length === 0 ? (
                  <div className="p-8 text-center text-[#A1A1AA]">
                    No messages match your search
                  </div>
                ) : (
                  filteredMessages.map((msg) => (
                    <motion.div
                      key={msg.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className={`p-4 border-b border-[rgba(212,175,55,0.1)] cursor-pointer transition-colors ${
                        selectedMessage?.id === msg.id 
                          ? 'bg-[rgba(212,175,55,0.1)]' 
                          : 'hover:bg-[rgba(212,175,55,0.05)]'
                      } ${!msg.read ? 'border-l-4 border-l-[#D4AF37]' : ''}`}
                      onClick={() => {
                        setSelectedMessage(msg);
                        if (!msg.read) markAsRead(msg.id);
                      }}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            {!msg.read && (
                              <span className="w-2 h-2 bg-[#D4AF37] rounded-full flex-shrink-0" />
                            )}
                            <h4 className={`font-semibold truncate ${!msg.read ? 'text-[#F5F5F0]' : 'text-[#A1A1AA]'}`}>
                              {msg.name}
                            </h4>
                          </div>
                          <p className="text-sm text-[#A1A1AA] truncate">{msg.email}</p>
                          <p className="text-sm text-[#a1a1aa] mt-1 line-clamp-2">{msg.message}</p>
                        </div>
                        <div className="text-xs text-[#a1a1aa] whitespace-nowrap">
                          {formatDate(msg.created_at)}
                        </div>
                      </div>
                    </motion.div>
                  ))
                )}
              </div>
            </div>

            {/* Message Detail */}
            <div className="bg-[#121212] border border-[rgba(212,175,55,0.15)] p-6">
              {selectedMessage ? (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <div className="flex justify-between items-start mb-6">
                    <div>
                      <h3 className="text-xl font-['Playfair_Display'] font-semibold text-[#F5F5F0]">
                        {selectedMessage.name}
                      </h3>
                      <p className="text-[#D4AF37]">{selectedMessage.email}</p>
                    </div>
                    <div className="flex gap-2">
                      {selectedMessage.read ? (
                        <button
                          onClick={() => markAsUnread(selectedMessage.id)}
                          className="p-2 text-[#A1A1AA] hover:text-[#D4AF37] hover:bg-[rgba(212,175,55,0.1)] rounded transition-colors"
                          title="Mark as unread"
                        >
                          <Circle className="w-5 h-5" />
                        </button>
                      ) : (
                        <button
                          onClick={() => markAsRead(selectedMessage.id)}
                          className="p-2 text-[#A1A1AA] hover:text-[#D4AF37] hover:bg-[rgba(212,175,55,0.1)] rounded transition-colors"
                          title="Mark as read"
                        >
                          <CheckCircle className="w-5 h-5" />
                        </button>
                      )}
                      <button
                        onClick={() => deleteMessage(selectedMessage.id)}
                        className="p-2 text-[#A1A1AA] hover:text-red-500 hover:bg-red-500/10 rounded transition-colors"
                        title="Delete message"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  <div className="space-y-4 mb-6">
                    {selectedMessage.phone && (
                      <div className="flex items-center gap-3 text-[#A1A1AA]">
                        <Phone className="w-4 h-4 text-[#D4AF37]" />
                        <a href={`tel:${selectedMessage.phone}`} className="hover:text-[#D4AF37]">
                          {selectedMessage.phone}
                        </a>
                      </div>
                    )}
                    <div className="flex items-center gap-3 text-[#A1A1AA]">
                      <Clock className="w-4 h-4 text-[#D4AF37]" />
                      <span>
                        {new Date(selectedMessage.created_at).toLocaleDateString('en-US', {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                  </div>

                  <div className="border-t border-[rgba(212,175,55,0.15)] pt-6">
                    <h4 className="text-sm text-[#D4AF37] uppercase tracking-wider mb-3 flex items-center gap-2">
                      <MessageSquare className="w-4 h-4" />
                      Message
                    </h4>
                    <p className="text-[#F5F5F0] whitespace-pre-wrap leading-relaxed">
                      {selectedMessage.message}
                    </p>
                  </div>

                  {/* Reply Button */}
                  <div className="mt-6 pt-6 border-t border-[rgba(212,175,55,0.15)]">
                    <a
                      href={`mailto:${selectedMessage.email}?subject=Re: Your inquiry to Mix & Mingle`}
                      className="btn-primary inline-flex items-center gap-2"
                    >
                      <Mail className="w-4 h-4" />
                      Reply via Email
                    </a>
                  </div>
                </motion.div>
              ) : (
                <div className="h-full flex flex-col items-center justify-center text-center py-12">
                  <Mail className="w-16 h-16 text-[#a1a1aa] mb-4" />
                  <h3 className="text-lg font-['Playfair_Display'] text-[#F5F5F0] mb-2">
                    Select a Message
                  </h3>
                  <p className="text-[#A1A1AA]">
                    Click on a message to view its details
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminMessagesPage;
